import { Box } from "@mui/material";
import { lighten } from "@mui/material/styles";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";
import { getTicks, getTooltip } from "../../utils/chartjsTheming";

/**
 * The height of the chart, in px, where what it stands in gives it none. In a
 * frame with a height of its own the chart fills the frame.
 */
const CHART_HEIGHT = 150;

/** How far the end of a bar is rounded, in px. */
const BAR_RADIUS = 4;

/** How far the bars are lightened from the primary colour. */
const BAR_LIGHTEN = 0.35;

function createPercentageBins(
  data: number[],
  numberOfBins: number,
): { label: string; count: number }[] {
  if (numberOfBins === 0) return [];
  const counts = new Array(numberOfBins).fill(0);
  const step = 1 / numberOfBins;

  data.forEach((progress) => {
    const index = Math.min(Math.floor(progress / step), numberOfBins - 1);
    counts[index]++;
  });

  const stepInPercent = step * 100;
  return counts.map((count, i) => {
    const from = Math.round(i * stepInPercent);
    const to = from + stepInPercent;
    return {
      label: `${from}-${to}%`,
      count,
    };
  });
}

/**
 * How many of the shares in `data` fall into each of a number of equal ranges
 * from 0 to 100%. `label` and `tooltip` are keys of the texts.
 */
export const HistogramChart = ({
  data,
  label,
  tooltip,
  backgroundColor,
  borderColor,
  bins = 10,
}: {
  data: number[];
  label: string;
  tooltip: string;
  bins?: number;
  backgroundColor?: string;
  borderColor?: string;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const binData = createPercentageBins(data, bins);

  const chartData = {
    labels: binData.map((bin) => bin.label),
    datasets: [
      {
        label: t(label),
        data: binData.map((bin) => bin.count),
        backgroundColor:
          backgroundColor ?? lighten(theme.palette.primary.main, BAR_LIGHTEN),
        // a bar is outlined only where a colour for it is given
        borderColor,
        borderWidth: borderColor ? 1 : 0,
        borderRadius: BAR_RADIUS,
      },
    ],
  };

  return (
    <Box sx={{ position: "relative", height: "100%", minHeight: CHART_HEIGHT }}>
      <Bar
        data={chartData}
        options={{
          // the chart fills the frame it is given rather than keeping a shape
          maintainAspectRatio: false,
          interaction: {
            mode: "index",
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              ...getTooltip(theme),
              displayColors: false,
              callbacks: {
                title: () => "",
                label: (item) =>
                  t(`${tooltip}.description`, {
                    count: item.parsed.y ?? 0,
                    range: item.label,
                  }),
              },
            },
          },
          scales: {
            x: {
              ticks: { ...getTicks(theme), maxRotation: 0 },
              grid: { display: false },
            },
            y: {
              beginAtZero: true,
              ticks: {
                ...getTicks(theme),
                precision: 0,
              },
              grid: {
                // the line the bars stand on stands out from the other lines
                color: (context) =>
                  context.tick.value === 0
                    ? theme.palette.text.disabled
                    : theme.palette.divider,
              },
            },
          },
        }}
      />
    </Box>
  );
};
